<script setup>
import { ref, computed } from "vue";
import { useFieldsStore } from "@/stores/storeFieldData";
import { useRequestsStore } from "@/stores/storeRequests";
import { useQuestionnairesStore } from "@/stores/storeQuestionnaires";

const storeField = useFieldsStore();
const storeRequests = useRequestsStore();
const storeQuestionnaires = useQuestionnairesStore();

const props = defineProps({
	oneLine: {
		type: Boolean,
		default: false,
	}
})

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_FILES_COUNT = 10;
const responseError = ref(null);
const filesCount = computed(() => storeField.fields.files.length);
const isMaxFilesReached = computed(() => filesCount.value >= MAX_FILES_COUNT);

const handleFiles = (e) => {
	const files = Array.from(e.target.files);
	let fileId = storeField.fields.files.length > 0 ? Math.max(...storeField.fields.files.map((f) => f.id)) : 0;

	const allowedFormats = [".jpg", ".jpeg", ".png", ".webp", ".tiff", ".pdf", ".gif", ".pdf"];

	// Фильтруем только те файлы, которые можно добавить (не превышая лимит)
	const filesToAdd = files.slice(0, MAX_FILES_COUNT - filesCount.value);

	if (filesToAdd.length === 0) {
		responseError.value = `Максимальное количество файлов (${MAX_FILES_COUNT}) уже достигнуто.`;
		return;
	}

	for (let file of filesToAdd) {
		const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

		if (!allowedFormats.includes(fileExtension)) {
			responseError.value = `Формат файла "${fileExtension}" не поддерживается.`;
			continue;
		}

		if (file.size <= MAX_FILE_SIZE) {
			fileId++;
			storeField.fields.files.push({
				id: fileId,
				nameFile: file.name,
				sizeFile: file.size,
				imgFile: window.URL.createObjectURL(file),
			});
			storeField.fields.filesForPost.push(file);
			responseError.value = null;
		} else {
			responseError.value = `Файл "${file.name}" превышает 5MB и не был добавлен.`;
		}
	}

	// Если попытались добавить больше файлов чем доступно
	if (files.length > filesToAdd.length) {
		responseError.value = `Добавлено ${filesToAdd.length} из ${files.length} файлов. Максимум ${MAX_FILES_COUNT} файлов.`;
	}
	e.target.value = "";
};

const deleteFile = (name) => {
	storeField.fields.files = storeField.fields.files.filter((file) => file.nameFile !== name);
	storeField.fields.filesForPost = storeField.fields.filesForPost.filter((file) => file.name !== name);
};
</script>
<template>
	<div class="file-holder">
		<label :class="['file', { '--disabled': isMaxFilesReached }]">
			<input 
				type="file" 
				class="file__real" 
				accept=".jpg, .jpeg, .png, .gif, .tiff, .webp, .pdf" 
				multiple @change="handleFiles" 
				:disabled="isMaxFilesReached" 
				required
			/>
			<span class="file__custom"></span>
			<span class="file__text" v-if="!isMaxFilesReached"><slot></slot></span>
			<span class="file__text" v-else>Максимум файлов достигнут</span>
		</label>
		<Transition mode="out-in">
            <span v-if="!!storeQuestionnaires.responseErrorFile" class="file-error">{{ storeQuestionnaires.responseErrorFile }}</span>
        </Transition>
		<ul class="file-desc__list" v-if="props.oneLine">
			<li class="file-desc__item">Разрешенные форматы файлов: .jpg, .jpeg, .png, .gif, .tiff, .webp, .pdf. Максимальный размер файла: 5 MB. Максимальное количество файлов: 5.</li>
		</ul>
		<ul class="file-desc__list" v-else>
			<li class="file-desc__item">Разрешенные форматы файлов: .jpg, .jpeg, .png, .gif, .tiff, .webp, .pdf.</li>
			<li class="file-desc__item">Максимальный размер файла: 5 MB. Максимальное количество файлов: 5.</li>
		</ul>
		<p class="file-error" v-if="!!responseError">{{ responseError }}</p>		
		<div class="preview-file">
			<TransitionGroup name="list" tag="ul" class="preview-file__list">
				<li class="preview-file__item" v-for="{ file, id, imgFile, nameFile } in storeField.fields.files" :key="id">
					<div class="preview-file__file-image">
						<img class="full-img" width="40" height="40" :src="imgFile" alt="изображение" />
					</div>
					<div class="preview-file__content">
						<p class="preview-file__file-name">{{ nameFile }}</p>
						<button class="preview-file__btn" type="button" @click="deleteFile(nameFile)">
							<svg width="20" height="20"><use href="#x"></use></svg>
						</button>
					</div>
				</li>
			</TransitionGroup>
		</div>
		
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.file-holder {
	display: grid;
	// gap: 10px;
}
.file {
	width: fit-content;
	display: flex;
	align-items: center;
	gap: 0.8rem;
	cursor: pointer;
	padding: 14px 0;
	@media (hover: hover) {
		&:hover {
			.file__text {
				text-decoration: underline;
			}
		}
	}
}
.file__real {
	width: 0;
	height: 0;
	opacity: 0;
	position: absolute;
	z-index: -1;
}
.file__custom {
	background-image: url("@/assets/img/icons/paperclip.svg");
	background-repeat: no-repeat;
	background-position: 0px 0px; /* x y*/
	background-size: 28px;
	width: 28px;
	height: 28px;
	flex-shrink: 0;
}

.file__text {
	font-weight: 400;
	font-size: 16px;
	line-height: 131%;
	letter-spacing: -0.01em;
	color: var(--native-link-color);
}
.file-desc__list {
	padding: 14px 0;
}
.file-desc__item {
	font-weight: 400;
	font-size: 1.5rem;
	line-height: 133%;
	color: var(--native-hint-color);
}
.preview-file {
	// margin-top: 20px;
}
.preview-file__list {
	display: grid;
}
.preview-file__item {
	position: relative;
	display: grid;
	grid-template-columns: 48px 1fr;
	gap: 20px;
	
}
.file-error {
	display: inline-block;
	margin-top: 10px;
	color: red;
	font-size: 1.6rem;
}
.preview-file__content {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 18px 0;
	border-bottom: 1px solid var(--custom-outline);
}
.preview-file__file-image {
	border: 0.50px solid var(--custom-outline);
	border-radius: 8px;
	width: 48px;
	height: 48px;
}
.preview-file__file-name {
	font-weight: 400;
	font-size: 1.6rem;
	line-height: 131%;
	letter-spacing: -0.01em;
	color: var(--native-text-color);
}
.preview-file__btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	background: var(--fill-default-1);
	svg {
		width: 24px;
		height: 24px;
		stroke-width: 2px;
		fill: transparent;
		stroke: var(--custom-secondary-hint-color);
	}
}

.list-enter-active,
.list-leave-active {
	transition: all 0.5s ease;
}
.list-enter-from,
.list-leave-to {
	opacity: 0;
	transform: translateX(30px);
}
</style>
